import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService, cityService, districtService } from '../api';
import { sortCities } from '../utils/sortCities';
import type { CityViewModel } from '../api/types/city';
import type { DistrictViewModel } from '../api/types/district';
import AuthHeader from '../components/Header/AuthHeader';
import Button from '../components/Button';

interface FormData {
  accountType: 'bireysel' | 'isletme';
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  companyType: 0 | 1;
  taxNumber: string;
  authorizedName: string;
  authorizedSurname: string;
  authorizedTcNo: string;
  companyEmail: string;
  phone: string;
  taxOfficeCityId: string;
  taxOfficeDistrictId: string;
  taxCertificate: File | null;
  termsAccepted: boolean;
}

const Kayit: React.FC = () => {
  const { register, registerCorporate } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    accountType: 'bireysel',
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    companyType: 0,
    taxNumber: '',
    authorizedName: '',
    authorizedSurname: '',
    authorizedTcNo: '',
    companyEmail: '',
    phone: '',
    taxOfficeCityId: '',
    taxOfficeDistrictId: '',
    taxCertificate: null,
    termsAccepted: false,
  });
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [districts, setDistricts] = useState<DistrictViewModel[]>([]);
  const [error, setError] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(sortCities(r.data));
    });
  }, []);

  useEffect(() => {
    const cityId = formData.taxOfficeCityId ? Number(formData.taxOfficeCityId) : null;
    if (!cityId) {
      setDistricts([]);
      return;
    }
    districtService.getAll({ cityId }).then((r) => {
      if (r.isSuccess && r.data) setDistricts(r.data);
    });
  }, [formData.taxOfficeCityId]);

  const handleGoogleSignUp = (): void => {
    window.location.href = authService.getGoogleLoginUrl();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setFormData((prev) => ({ ...prev, taxCertificate: file }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.termsAccepted) {
      setError('Kullanım koşullarını kabul etmeniz gerekmektedir.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Şifre ve şifre tekrar alanları eşleşmiyor.');
      return;
    }

    setSubmitting(true);
    try {
      if (formData.accountType === 'bireysel') {
        const trimmedName = formData.name.trim();
        const spaceIndex = trimmedName.indexOf(' ');
        const firstName = spaceIndex > 0 ? trimmedName.slice(0, spaceIndex) : trimmedName;
        const lastName = spaceIndex > 0 ? trimmedName.slice(spaceIndex + 1) : '';
        await register({
          firstName: firstName || undefined,
          lastName: lastName || undefined,
          email: formData.email || undefined,
          password: formData.password || undefined,
        });
      } else {
        if (!formData.taxCertificate) {
          setError('Vergi levhası görseli gereklidir.');
          return;
        }
        if (!formData.taxOfficeCityId || !formData.taxOfficeDistrictId) {
          setError('Vergi dairesi il ve ilçe seçimi gereklidir.');
          return;
        }
        if (formData.companyType === 0 && !formData.authorizedTcNo.trim()) {
          setError('Şahıs şirketi için T.C. kimlik numarası gereklidir.');
          return;
        }
        await registerCorporate({
          firstName: formData.authorizedName.trim(),
          lastName: formData.authorizedSurname.trim(),
          email: formData.companyEmail.trim(),
          password: formData.password,
          phoneNumber: formData.phone.trim(),
          companyName: formData.companyName.trim(),
          companyType: formData.companyType,
          taxNumber: formData.taxNumber.trim(),
          nationalNumber: formData.companyType === 0 ? formData.authorizedTcNo.trim() : undefined,
          taxOfficeCityId: Number(formData.taxOfficeCityId),
          taxOfficeDistrictId: Number(formData.taxOfficeDistrictId),
          taxCertificate: formData.taxCertificate,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  const isSoleProp = formData.companyType === 0;

  return (
    <>
      <AuthHeader />
      <div className="sb-page-bg min-vh-100">
        <div className="container">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 col-lg-5">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h4 className="card-title text-center mb-4">Kayıt Ol</h4>

                  {error && (
                    <div className="alert alert-danger mb-3" role="alert">
                      {error}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label className="form-label mb-3">Hesap Türü</label>
                      <div className="row g-3">
                        <div className="col-6">
                          <input
                            className="btn-check"
                            id="bireysel"
                            name="accountType"
                            type="radio"
                            value="bireysel"
                            checked={formData.accountType === 'bireysel'}
                            onChange={handleInputChange}
                          />
                          <label
                            className="btn btn-outline-primary w-100 d-flex flex-column align-items-center justify-content-center p-4"
                            htmlFor="bireysel"
                          >
                            <i className="fas fa-user fa-lg mb-2" />
                            <span className="fw-medium">Bireysel</span>
                            <small
                              style={{
                                fontSize: '0.75rem',
                                color: formData.accountType === 'bireysel' ? 'white' : '#0d6efd',
                              }}
                            >
                              Kişisel kullanım için
                            </small>
                          </label>
                        </div>
                        <div className="col-6">
                          <input
                            className="btn-check"
                            id="isletme"
                            name="accountType"
                            type="radio"
                            value="isletme"
                            checked={formData.accountType === 'isletme'}
                            onChange={handleInputChange}
                          />
                          <label
                            className="btn btn-outline-primary w-100 d-flex flex-column align-items-center justify-content-center p-4"
                            htmlFor="isletme"
                          >
                            <i className="fas fa-building fa-lg mb-2" />
                            <span className="fw-medium">İşletme</span>
                            <small
                              style={{
                                fontSize: '0.75rem',
                                color: formData.accountType === 'isletme' ? 'white' : '#0d6efd',
                              }}
                            >
                              Kurumsal kullanım için
                            </small>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: formData.accountType === 'bireysel' ? 'block' : 'none' }}>
                      <div className="mb-3">
                        <label className="form-label">Ad Soyad</label>
                        <input
                          className="form-control"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required={formData.accountType === 'bireysel'}
                          type="text"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">E-posta</label>
                        <input
                          className="form-control"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required={formData.accountType === 'bireysel'}
                          type="email"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Şifre</label>
                        <input
                          className="form-control"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required={formData.accountType === 'bireysel'}
                          type="password"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Şifre Tekrar</label>
                        <input
                          className="form-control"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          required={formData.accountType === 'bireysel'}
                          type="password"
                        />
                      </div>
                    </div>

                    <div style={{ display: formData.accountType === 'isletme' ? 'block' : 'none' }}>
                      <div className="mb-3">
                        <label className="form-label">Marka Adı</label>
                        <input
                          className="form-control"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          placeholder="Mağazanızda görünecek marka adı"
                          required={formData.accountType === 'isletme'}
                          type="text"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-medium">Yetkili Kişi Bilgileri</label>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <input
                              className="form-control"
                              name="authorizedName"
                              value={formData.authorizedName}
                              onChange={handleInputChange}
                              placeholder="Ad"
                              required={formData.accountType === 'isletme'}
                              type="text"
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <input
                              className="form-control"
                              name="authorizedSurname"
                              value={formData.authorizedSurname}
                              onChange={handleInputChange}
                              placeholder="Soyad"
                              required={formData.accountType === 'isletme'}
                              type="text"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">İşletme E-posta</label>
                        <input
                          className="form-control"
                          name="companyEmail"
                          value={formData.companyEmail}
                          onChange={handleInputChange}
                          required={formData.accountType === 'isletme'}
                          type="email"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Telefon</label>
                        <input
                          className="form-control"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required={formData.accountType === 'isletme'}
                          type="tel"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Şifre</label>
                        <input
                          className="form-control"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required={formData.accountType === 'isletme'}
                          type="password"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Şifre Tekrar</label>
                        <input
                          className="form-control"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          required={formData.accountType === 'isletme'}
                          type="password"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Şirket Türü</label>
                        <div className="d-flex flex-column gap-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              id="companyTypeSole"
                              name="companyType"
                              type="radio"
                              value={0}
                              checked={formData.companyType === 0}
                              onChange={() =>
                                setFormData((prev) => ({ ...prev, companyType: 0 }))
                              }
                            />
                            <label className="form-check-label" htmlFor="companyTypeSole">
                              Şahıs şirketi
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              id="companyTypeLimited"
                              name="companyType"
                              type="radio"
                              value={1}
                              checked={formData.companyType === 1}
                              onChange={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  companyType: 1,
                                  authorizedTcNo: '',
                                }))
                              }
                            />
                            <label className="form-check-label" htmlFor="companyTypeLimited">
                              Limited / Anonim şirket
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Vergi Numarası</label>
                        <input
                          className="form-control"
                          name="taxNumber"
                          value={formData.taxNumber}
                          onChange={handleInputChange}
                          required={formData.accountType === 'isletme'}
                          type="text"
                        />
                      </div>
                      {isSoleProp && (
                        <div className="mb-3">
                          <label className="form-label">T.C. Kimlik No</label>
                          <input
                            className="form-control"
                            name="authorizedTcNo"
                            value={formData.authorizedTcNo}
                            onChange={handleInputChange}
                            required={formData.accountType === 'isletme' && isSoleProp}
                            type="text"
                          />
                        </div>
                      )}
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label className="form-label">Vergi Dairesi İli</label>
                          <select
                            className="form-select"
                            name="taxOfficeCityId"
                            value={formData.taxOfficeCityId}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                taxOfficeCityId: e.target.value,
                                taxOfficeDistrictId: '',
                              }))
                            }
                            required={formData.accountType === 'isletme'}
                          >
                            <option value="">İl seçin</option>
                            {cities.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Vergi Dairesi İlçesi</label>
                          <select
                            className="form-select"
                            name="taxOfficeDistrictId"
                            value={formData.taxOfficeDistrictId}
                            onChange={handleInputChange}
                            required={formData.accountType === 'isletme'}
                            disabled={!formData.taxOfficeCityId}
                          >
                            <option value="">İlçe seçin</option>
                            {districts.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Vergi Levhası</label>
                        <input
                          accept="image/jpeg,image/png,image/webp"
                          className="form-control"
                          name="taxCertificate"
                          onChange={handleFileChange}
                          required={formData.accountType === 'isletme'}
                          type="file"
                        />
                        <div className="form-text">JPEG, PNG veya WebP formatında yükleyin.</div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          name="termsAccepted"
                          checked={formData.termsAccepted}
                          onChange={handleInputChange}
                          required
                          type="checkbox"
                        />
                        <label className="form-check-label">
                          <Link to="/kullanici-sozlesmesi" style={{ marginRight: '5px' }}>
                            Kullanım koşullarını
                          </Link>
                          kabul ediyorum
                        </label>
                      </div>
                    </div>

                    <Button
                      className="btn btn-primary w-100 mb-3"
                      type="submit"
                      disabled={submitting}
                      loading={submitting}
                    >
                      Kayıt Ol
                    </Button>

                    {formData.accountType === 'bireysel' && (
                      <>
                        <div className="text-center mb-3">
                          <span className="text-muted">veya</span>
                        </div>
                        <Button
                          className="btn sb-oauth-google-btn w-100 mb-3"
                          type="button"
                          onClick={handleGoogleSignUp}
                        >
                          <i className="fab fa-google me-2" style={{ fontSize: '1.1rem' }} aria-hidden />
                          Google ile Kayıt Ol
                        </Button>
                      </>
                    )}

                    <div className="text-center">
                      <span className="text-muted">Zaten hesabınız var mı?</span>
                      <Link className="text-decoration-none ms-1" to="/giris">
                        Giriş Yap
                      </Link>
                    </div>
                    <div className="text-center mt-2">
                      <Link className="text-decoration-none small" to="/sifremi-unuttum">
                        Şifremi Unuttum
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Kayit;
